/**
 * **THE currency module**: everything about how money is shown, in one place.
 *
 * Three tiers, the same options everywhere:
 *
 * | Where you are                          | What to use                                              |
 * |----------------------------------------|----------------------------------------------------------|
 * | a React component                      | `useMoney()`, `useAmount()`, `useCurrencySymbol()`, `useMoneySettings()` (`./hooks`) |
 * | a helper, saga, middleware or handler  | `money()`, `amount()`, `currencySymbol()`, `moneySettings()` (below) |
 * | code that must not read the store      | `formatMoney()`, `formatAmount()`, `currencySymbolFor()` (`./format`, `./symbol`) |
 *
 * The plain functions below read the store ONCE, when called; they do not re-render anything. That
 * is why a component uses the hooks. Both read through the same selector (`./selectors`).
 *
 * 🔴 **Display only, never money maths.** See `./format`'s header.
 */
import store from "../redux-store";
import { formatAmount, formatMoney } from "./format";
import type { MoneyFormat, MoneyOptions, MoneySettings } from "./format";
import { selectMoneySettings } from "./selectors";

// A star re-export, so `@rista/core/currency` answers everything and no hand-kept list can drift.
// ⚠️ It creates no local binding: names used below are imported above as well (ledger rule 5).
export * from "./format";
export * from "./symbol";
export * from "./selectors";
export * from "./hooks";

/**
 * This merchant's `MoneySettings`, read now. For a host outside React that hands the format across
 * a boundary (the customer-display middleware).
 *
 * Reads the store INSIDE the body, never at module scope: a module-eval-time read breaks the
 * bundle through core's import cycles (`pnpm audit:uplift` checks H/I/J).
 *
 * @example moneySettings() // { symbol: "₹", decimals: 2, commaStyle: "lakhs" }
 */
export const moneySettings = (): MoneySettings => selectMoneySettings(store.getState());

/**
 * The currency symbol for this business, read now.
 *
 * @example currencySymbol() // "₹"
 */
export const currencySymbol = (): string => moneySettings().symbol;

/**
 * A complete money string for this business, read now.
 *
 * ⚠️ **Never feed the result back in.** It is text: `money(money(1200))` is `"₹0.00"`.
 *
 * @example money(1234.5)                     // "₹1,234.50"
 * @example money(100, { decimals: 0 })        // "₹100"
 * @example money(120000, { commaStyle: "none" }) // "₹120000.00"
 */
export const money: MoneyFormat = (value, options) => formatMoney(value, moneySettings(), options);

/**
 * `money()` without the symbol: the digits only, for a screen that styles the symbol itself.
 *
 * ⚠️ `amount` is a common local variable name; a file that already has one renames the import
 * (`import { amount as amountDigits }`): a compile error, never a silent bug.
 *
 * @example amount(1234.5) // "1,234.50"
 */
export const amount = (value: unknown, options?: MoneyOptions): string =>
  formatAmount(value, moneySettings(), options);
